"use client"

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Container } from "@/components/layout/Container"
import { getReferences } from "@/lib/cms/queries"
import type { Reference } from "@/types/database"
import { stripHTML } from "@/lib/utils"
import Image from "next/image"
import Link from "next/link"
import { ArrowRight, ExternalLink, Calendar, Tag, User, Briefcase, Filter, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"

export default function ReferencesPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  const [references, setReferences] = useState<Reference[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  useEffect(() => {
    loadReferences()
  }, [currentLocale])

  const loadReferences = async () => {
    try {
      const refs = await getReferences('published', currentLocale)
      // getReferences zaten featured'ları önce getiriyor, ama yine de sıralama yapalım
      setReferences(refs)
    } catch (error) {
      console.error("Error loading references:", error)
    } finally {
      setLoading(false)
    }
  }

  // Kategorileri temizle ve birleştir
  const allCategories = Array.from(
    new Set(
      references
        .flatMap(ref => ref.category || [])
        .filter(Boolean)
    )
  ) as string[]

  // Benzer kategorileri birleştir (normalize et)
  const normalizeCategory = (cat: string): string => {
    const lower = cat.toLowerCase().trim()
    
    // WooCommerce kategorilerini birleştir
    if (lower.includes('woocommerce')) {
      if (lower.includes('tasarım') || lower.includes('geliştirme')) {
        return 'WooCommerce'
      }
      return 'WooCommerce'
    }
    
    // Shopify kategorilerini birleştir
    if (lower.includes('shopify')) {
      if (lower.includes('tasarım') || lower.includes('geliştirme')) {
        return 'Shopify'
      }
      return 'Shopify'
    }
    
    // E-ticaret kategorilerini birleştir
    if (lower.includes('e-ticaret') || lower === 'e-ticaret sistemleri') {
      return 'E-ticaret'
    }
    
    // AI kategorilerini birleştir
    if (lower.includes('ai') || lower.includes('yapay zeka')) {
      return 'AI Servisler'
    }
    
    return cat
  }

  // Kategorileri normalize et ve tekrarları temizle
  const normalizedCategories = allCategories.map(normalizeCategory)
  const uniqueCategories = Array.from(new Set(normalizedCategories))
  
  // Kategori sıralama önceliği
  const categoryOrder = [
    'Web Tasarım',
    'Web Yazılım',
    'UI & UX Tasarım',
    'E-ticaret',
    'WooCommerce',
    'Shopify',
    'Mobil Uygulama',
    'Masaüstü Uygulama',
    'Otomasyon',
    'AI Servisler',
  ]

  // Kategorileri öncelik sırasına göre sırala
  const organizedCategories = uniqueCategories.sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a)
    const bIndex = categoryOrder.indexOf(b)
    
    // Öncelik listesinde varsa öncelik sırasına göre sırala
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1
    
    // Öncelik listesinde yoksa alfabetik sırala
    return a.localeCompare(b, 'tr')
  })

  // Kategori filtresi uygula (normalize edilmiş kategorilerle eşleştir)
  const filteredReferences = (selectedCategory
    ? references.filter(ref => {
        if (!ref.category || ref.category.length === 0) return false
        // Normalize edilmiş kategori ile eşleşen tüm kategorileri bul
        return ref.category.some(cat => normalizeCategory(cat) === selectedCategory)
      })
    : references
  ).sort((a, b) => {
    if (a.featured && !b.featured) return -1
    if (!a.featured && b.featured) return 1
    return 0
  })

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Header */}
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex p-4 bg-white/10 rounded-lg mb-6"
          >
            <Briefcase className="h-12 w-12" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6 font-mono"
          >
            Referanslarımız
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/90 sm:text-xl"
          >
            Başarılı projelerimizden örnekler. Müşterilerimizin dijital dönüşüm hikayelerini keşfedin ve markanız için ilham alın.
          </motion.p>
        </div>
      </Section>

      {/* References Content */}
      <Section className="py-16 sm:py-20 md:py-24 lg:py-32">
        <Container>

          {/* Category Filter */}
          {organizedCategories.length > 0 && (
            <div className="mb-8 sm:mb-12">
              {/* Desktop View - Always Visible */}
              <div className="hidden md:block">
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                  <span className="text-sm sm:text-base font-semibold text-slate-700">Filtrele:</span>
                  <span className="text-xs sm:text-sm text-slate-500">
                    ({filteredReferences.length} {filteredReferences.length === 1 ? 'proje' : 'proje'})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <Button
                    variant={selectedCategory === null ? "default" : "outline"}
                    onClick={() => setSelectedCategory(null)}
                    className="rounded-full text-sm px-4 py-2 h-auto font-medium"
                  >
                    Tümü
                  </Button>
                  {organizedCategories.map((category) => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? "default" : "outline"}
                      onClick={() => setSelectedCategory(category)}
                      className="rounded-full text-sm px-4 py-2 h-auto font-medium whitespace-nowrap"
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Mobile View - Drawer */}
              <div className="md:hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700">Filtrele</span>
                    <span className="text-xs text-slate-500">
                      ({filteredReferences.length} {filteredReferences.length === 1 ? 'proje' : 'proje'})
                    </span>
                  </div>
                  <Drawer open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                    <DrawerTrigger asChild>
                      <Button
                        variant={selectedCategory ? "default" : "outline"}
                        className="rounded-full text-sm px-4 py-2 h-auto font-medium flex items-center gap-2"
                      >
                        <Filter className="h-4 w-4" />
                        {selectedCategory ? selectedCategory : "Kategoriler"}
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent>
                      <DrawerHeader className="text-left">
                        <div className="flex items-center justify-between">
                          <DrawerTitle>Kategorileri Filtrele</DrawerTitle>
                          <DrawerClose asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <X className="h-4 w-4" />
                            </Button>
                          </DrawerClose>
                        </div>
                      </DrawerHeader>
                      <div className="px-4 pb-6">
                        <div className="flex flex-col gap-3">
                          <Button
                            variant={selectedCategory === null ? "default" : "outline"}
                            onClick={() => {
                              setSelectedCategory(null)
                              setIsFilterOpen(false)
                            }}
                            className="rounded-full text-sm px-4 py-2 h-auto font-medium w-full justify-start"
                          >
                            Tümü
                          </Button>
                          {organizedCategories.map((category) => (
                            <Button
                              key={category}
                              variant={selectedCategory === category ? "default" : "outline"}
                              onClick={() => {
                                setSelectedCategory(category)
                                setIsFilterOpen(false)
                              }}
                              className="rounded-full text-sm px-4 py-2 h-auto font-medium w-full justify-start"
                            >
                              {category}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </DrawerContent>
                  </Drawer>
                </div>
              </div>
            </div>
          )}

          {/* References Grid */}
          {loading ? (
            <div className="text-center py-12 text-slate-500">Yükleniyor...</div>
          ) : filteredReferences.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>Henüz referans bulunmuyor.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filteredReferences.map((ref, index) => (
                <motion.div
                  key={ref.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link href={`/referanslar/${ref.slug || ref.id}`}>
                    <div className="group relative overflow-hidden bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer h-full flex flex-col">
                      {/* Image */}
                      <div className="relative aspect-[4/3] w-full overflow-hidden">
                        {(() => {
                          // If no image URL, show placeholder
                          if (!ref.image || ref.image.trim() === "") {
                            return (
                              <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 flex items-center justify-center">
                                <div className="text-center p-4">
                                  <div className="text-4xl mb-2">📷</div>
                                  <p className="text-xs text-slate-500 font-medium">Görsel Eklenecek</p>
                                </div>
                              </div>
                            );
                          }
                          
                          const isVideo = /\.(webm|mp4|mov|avi|mkv)$/i.test(ref.image);
                          if (isVideo) {
                            return (
                              <video
                                src={ref.image}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                muted
                                loop
                                playsInline
                                autoPlay
                                preload="none"
                              />
                            );
                          }
                          return (
                            <Image
                              src={ref.image}
                              alt={ref.title}
                              fill
                              className="object-cover transition-transform duration-700 group-hover:scale-110"
                              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                              loading="lazy"
                            />
                          );
                        })()}
                        
                        {/* Base Overlay - Always visible with site primary color */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/60 via-[rgb(3,74,166)]/40 to-[rgb(3,74,166)]/20 transition-opacity duration-500" />
                        
                        {/* Overlay on Hover - Darker site primary color */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(3,74,166)]/95 via-[rgb(3,74,166)]/70 to-[rgb(3,74,166)]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        {ref.featured && (
                          <div className="absolute top-4 left-4 bg-[rgb(3,74,166)] text-white px-3 py-1 rounded-full text-xs font-medium z-10">
                            Öne Çıkan
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="mb-4">
                          {ref.category && ref.category.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {ref.category.map((cat, index) => (
                                <span key={index} className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}
                          <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2">{ref.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2">{stripHTML(ref.description || '')}</p>
                        </div>

                        {/* Meta Info */}
                        <div className="mt-auto space-y-2">
                          {ref.client_name && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <User className="h-3 w-3" />
                              <span>{ref.client_name}</span>
                            </div>
                          )}
                          {ref.completion_date && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(ref.completion_date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' })}</span>
                            </div>
                          )}
                          {ref.technologies && ref.technologies.length > 0 && (
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Tag className="h-3 w-3" />
                              <span className="line-clamp-1">{ref.technologies.join(', ')}</span>
                            </div>
                          )}
                        </div>

                        {/* Link */}
                        <div className="mt-4 flex items-center text-[rgb(3,74,166)] font-medium group-hover:gap-2 transition-all">
                          <span className="text-sm">Detayları Gör</span>
                          <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </Container>
      </Section>
      <CTA locale={currentLocale} />
    </div>
  )
}

